import JSZip from 'jszip';
import { useCallback, useRef, useState } from 'react';
import { formatFileSize, parseJSON } from '@/shared/commonFunctions';
import {
  mergeByDatasetScope,
  mergeCatalogByProductCode,
  mergeClientsByCodePreserveStores,
  mergeDataValues,
  mergePrecoByScope,
  mergePrecoPromocaoClientePartial,
  mergeParametroPrecoBasePartial,
  reducePromocaoClienteArrayToNestedMap,
  shouldDownloadUpdatedDataset,
} from '@/shared/datasetIncrementalMerge';
import pacote from '../../../package.json';
import { ensureClientsGroupedByLoja } from '@/utils/clientDataTransform';
import { useEvents, getDateTimeMS } from '../use-events/useEvents';
import { unregisterServiceWorker } from '@/shared/serviceWorkFunctions';
import {
  extractAtomicFailureTableKey,
  getKnownDatasetUrls,
  getOnStorage,
  isImageDataset,
  isUnauthorizedAxiosError,
  mergeDatasetsByUrl,
  mergeEstoquePreserveExistingStores,
  migrateImageDatasetsFromMain,
  OBRIGATORY_EMPTY_BLOCK_REASON,
  removeFromStorage,
  resolveImageDatasetUrlsForSync,
  setOnStorage,
  writeImageDatasets,
} from '@/shared';
import type { InconsistencyProblem } from '@/shared/inconsistenciesData';
import { useClient, useFetchAxios, useGlobals, useUserContext } from '@/hooks';
import { Tables, toCoded, useDbFunctions, isValidDbPayload } from '../dbFunctions/useDBFunctions';
import {
  tableKeys,
  TableTypes,
  CategoryProps,
  DataSetsProps,
  ProductProps,
  CustomerDataProps,
  EventsClientProps,
  DicUltimaCompraProps,
  CampanhaProdutosProps,
  PrecoPromocaoClienteProps,
  ClientProps,
  PageBuilderProps,
  SalesCycleProps,
  AmbientTypes,
} from '@/types';

type CatalogDictProps = Record<number, ProductProps>;
type CategoryDictProps = Record<number, CategoryProps>;
type DownloadEventProps = Record<string, { status: 0 | 1, arquivo?: string, dataFim?: string, dataInicio?: string }>;

type DownloadMemProfile = {
  catalogChunkSize: number;
  clientsChunkSize: number;
  maxConcurrentDownloads: number;
};

type DatasetDownloadResult = {
  data: any;
  name: string;
  type: string;
  datasetKey: string;
};

type FetchDatasetsResponse = {
  byType: Record<string, { data: any }>;
  byDatasetKey: Record<string, DatasetDownloadResult>;
};

type DatasetWithStoreMeta = DataSetsProps & {
  COD_LOJA?: number | string | null;
  COD_SEGMENTO?: number | string | null;
};

const DEFAULT_MEM_PROFILE: DownloadMemProfile = {
  catalogChunkSize: 400,
  clientsChunkSize: 300,
  maxConcurrentDownloads: 2,
};

/**
 * Em aparelhos com pouca RAM, sync grande + ZIP + IDB pode estourar memória e o SO mata o PWA.
 * `navigator.deviceMemory` (GB) existe no Chrome/Android; Safari costuma não expor — use
 * `localStorage.setItem('pwa_sync_low_memory','1')` no mesmo origin para forçar perfil mínimo.
 */
function getDownloadMemoryTuning(): DownloadMemProfile {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('pwa_sync_low_memory') === '1') {
      return {
        catalogChunkSize: 150,
        clientsChunkSize: 120,
        maxConcurrentDownloads: 1,
      };
    }
  } catch {
    /* storage indisponível */
  }

  if (typeof navigator === 'undefined') return DEFAULT_MEM_PROFILE;

  const gb = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  if (gb !== undefined && gb <= 2) {
    return {
      catalogChunkSize: 150,
      clientsChunkSize: 120,
      maxConcurrentDownloads: 1,
    };
  }
  if (gb !== undefined && gb <= 4) {
    return {
      catalogChunkSize: 280,
      clientsChunkSize: 220,
      maxConcurrentDownloads: 1,
    };
  }
  return DEFAULT_MEM_PROFILE;
}

const _memTune = getDownloadMemoryTuning();
const CATALOG_CHUNK_SIZE = _memTune.catalogChunkSize;
const CLIENTS_CHUNK_SIZE = _memTune.clientsChunkSize;
const MAX_CONCURRENT_DOWNLOADS = _memTune.maxConcurrentDownloads;
const PROGRESS_THROTTLE_MS = 150;
const GETDATASET_TIMEOUT_MS = 45000;
const SPEED_UPDATE_INTERVAL_MS = 500;
const GETDATASET_STOCK_TIMEOUT_MS = 15000;
let globalSyncInProgress = false;

const OBLIGATORY_DB_TABLES: Tables[] = [
  'marcas',
  'preco',
  'estoque',
  'catalogo',
  'cliente',
  'categoria',
  'parametro_preco_base',
  'percentual_fornecedor',
  'cod_regime_tributario',
];

const TABLE_DATASET_TYPES: Partial<Record<Tables, string[]>> = {
  preco: ['preco', 'preco_novo'],
  cod_regime_tributario: ['cod_regime_tributario', 'substituicao_tributaria_aws'],
};

const progressLastCall: Record<string, number> = {};
const progressPercentSetterRef: { current: (n: number) => void } = { current: () => {} };

const runWithConcurrency = async <T>(tasks: (() => Promise<T>)[], max: number): Promise<PromiseSettledResult<T>[]> => {
  const results: PromiseSettledResult<T>[] = [];
  let index = 0;
  const runNext = async (): Promise<void> => {
    const i = index++;
    if (i >= tasks.length) return;
    try {
      const value = await tasks[i]();
      results[i] = { status: 'fulfilled', value };
    } catch (reason) {
      results[i] = { status: 'rejected', reason };
    }
    await runNext();
  };
  const workers = Array.from({ length: Math.min(max, tasks.length) }, () => runNext());
  await Promise.all(workers);
  return results;
};

const mountCategories = (campanha: CampanhaProdutosProps) => {
  if (!campanha) return [];

  return [
    {
      NRO_HIERARQUIA: 1,
      DE_CATEGORIA: campanha.nome,
      IMAGE: campanha?.inativo?.selo || '',
      PATH_CATEGORIA: campanha.url_amigavel,
    },
  ] as CategoryProps[];
};

const mergeProductPrices = (current: ProductProps['PRECO'], incoming: ProductProps['PRECO']) => {
  const byKey = new Map<string, ProductProps['PRECO'][number]>();
  [...(current || []), ...(incoming || [])].forEach((price) => {
    if (!price) return;
    const key = [
      Number(price.COD_LOJA || 0),
      Number(price.NRO_SEGMENTO || 0),
      Number(price.NROCONDICAOPAGTO || 0),
      Number(price.QTD_EMBALAGEM || 0),
      Number(price.QTD_MULTIPLO_VENDA || 0),
      `${price.SG_EMBALAGEM || ''}`,
      `${price.TP_PROMO || ''}`,
      `${price.DT_INICIO || ''}`,
      `${price.DT_FIM || ''}`,
    ].join(':');
    byKey.set(key, price);
  });
  return Array.from(byKey.values());
};

const mergeProductPackages = (current: ProductProps['EMBALAGEM'], incoming: ProductProps['EMBALAGEM']) => {
  const byKey = new Map<string, ProductProps['EMBALAGEM'][number]>();
  [...(current || []), ...(incoming || [])].forEach((pack) => {
    if (!pack) return;
    const key = [
      Number(pack.COD_LOJA || 0),
      Number(pack.COD_SEGMENTO || 0),
      Number(pack.QTD_EMBALAGEM || 0),
      Number(pack.QTD_MULTIPLO_VENDA || 0),
      `${pack.SG_EMBALAGEM || ''}`,
    ].join(':');
    byKey.set(key, pack);
  });
  return Array.from(byKey.values());
};

const mergeClientLojas = (current: ClientProps['lojas'], incoming: ClientProps['lojas']) => {
  const byKey = new Map<string, NonNullable<ClientProps['lojas']>[number]>();
  [...(current || []), ...(incoming || [])].forEach((loja) => {
    if (!loja) return;
    const segmentos = Array.isArray(loja.COD_SEGMENTO) ? loja.COD_SEGMENTO : [];
    const segmentosKey = segmentos
      .map((segmento: any) => Number(segmento?.cod_segmento ?? segmento?.COD_SEGMENTO ?? segmento))
      .filter((n: number): n is number => Number.isFinite(n))
      .join(',');
    const key = `${Number(loja.COD_LOJA || 0)}:${segmentosKey}`;
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, { ...loja });
      return;
    }
    byKey.set(key, { ...prev, ...loja });
  });

  return Array.from(byKey.values());
};

const mergeProductsByCode = (current: ProductProps | undefined, incoming: ProductProps): ProductProps => {
  if (!current) return incoming;

  return {
    ...current,
    ...incoming,
    PRECO: mergeProductPrices(current.PRECO, incoming.PRECO),
    EMBALAGEM: mergeProductPackages(current.EMBALAGEM, incoming.EMBALAGEM),
    ATRIBUTOS: (incoming.ATRIBUTOS?.length ? incoming.ATRIBUTOS : current.ATRIBUTOS) || [],
    IMAGEM: incoming.IMAGEM || current.IMAGEM || '',
    ultimaCompra: incoming.ultimaCompra || current.ultimaCompra || {},
  };
};

const updateProgress = (loaded: number, total: number, name: string) => {
  const now = Date.now();
  if (progressLastCall[name] != null && now - progressLastCall[name] < PROGRESS_THROTTLE_MS) return;
  progressLastCall[name] = now;

  const percentual = total > 0 ? Math.min((loaded / total) * 100, 100) : 0;
  const progressBar = document.getElementById(`${name}-bar`) as HTMLElement;
  const progressText = document.getElementById(`${name}-text`) as HTMLElement;
  const isValid = Number.isFinite(percentual);

  if (name === 'progress') {
    progressPercentSetterRef.current(percentual);
  } else {
    if (progressBar) progressBar.style.width = `${percentual}%`;
  }
  if (progressText) progressText.textContent = isValid ? `${Math.round(percentual)}%` : 'Erro no Download';
};

const getProductCodeFromFilename = (filename: string) => {
  const onlyNumbers = filename.split('/').pop()?.replace(/\D+/g, '');
  const code = Number(onlyNumbers);
  return Number.isFinite(code) ? code : null;
};

const isZipContent = (resp: Response, url: string) => {
  const ct = resp.headers.get('content-type') || '';
  return ct.includes('zip') || url.toLowerCase().endsWith('.zip');
};

const yieldToMain = (): Promise<void> =>
  new Promise((resolve) => {
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(() => resolve(), { timeout: 50 });
    } else {
      setTimeout(resolve, 0);
    }
});

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
});

const DATASET_TYPE_MAP: Record<string, string> = {
  'imagens_zip': 'imagens',
};

const normalizeDatasetType = (tipo: string) => DATASET_TYPE_MAP[tipo] || tipo;

const getDatasetKey = (dataset: DatasetWithStoreMeta, fallbackIndex?: number) =>
  [
    normalizeDatasetType(dataset.TIPO_ARQUIVO),
    dataset.COD_LOJA ?? 'null',
    dataset.COD_SEGMENTO ?? 'null',
    dataset.URL_S3 || fallbackIndex || 'no-url',
  ].join('::');

const resolveDatasetTypesForTable = (table: Tables): string[] => TABLE_DATASET_TYPES[table] ?? [table];

const wasTableAttemptedInRun = (table: Tables, typesAttempted: Set<string>) =>
  resolveDatasetTypesForTable(table).some((type) => typesAttempted.has(type));

const appendUniqueDatasets = (target: DataSetsProps[], toAdd: DataSetsProps[]) => {
  const keys = new Set(target.map((dataset, index) => getDatasetKey(dataset as DatasetWithStoreMeta, index)));
  toAdd.forEach((dataset) => {
    const key = getDatasetKey(dataset as DatasetWithStoreMeta);
    if (keys.has(key)) return;
    target.push(dataset);
    keys.add(key);
  });
};

const getDatasetProgressLabel = (dataset: DatasetWithStoreMeta) => {
  const base = dataset.DESC_ARQUIVO || normalizeDatasetType(dataset.TIPO_ARQUIVO);
  const loja = dataset.COD_LOJA != null && String(dataset.COD_LOJA).trim() !== '' ? String(dataset.COD_LOJA) : null;
  const segmento =
    dataset.COD_SEGMENTO != null && String(dataset.COD_SEGMENTO).trim() !== ''
      ? String(dataset.COD_SEGMENTO)
      : null;

  if (!loja && !segmento) return base;
  if (loja && segmento) return `${base} - Loja ${loja} / Segmento ${segmento}`;
  if (loja) return `${base} - Loja ${loja}`;
  return `${base} - Segmento ${segmento}`;
};

const isPlainObject = (value: any): value is Record<string, any> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const normalizeStockItem = (item: any) => {
  if (!isPlainObject(item)) return null;
  const codProdutoRaw = item.COD_PRODUTO ?? item.cod_produto;
  const qtdEstoqueRaw = item.QTD_ESTOQUE ?? item.qtd_estoque;
  if (codProdutoRaw == null || qtdEstoqueRaw == null) return null;

  const codProduto = Number(codProdutoRaw);
  const qtdEstoque = Number(qtdEstoqueRaw);
  if (!Number.isFinite(codProduto) || !Number.isFinite(qtdEstoque)) return null;

  const codLojaRaw = item.COD_LOJA ?? item.cod_loja;
  const codLoja = codLojaRaw == null || codLojaRaw === '' ? null : Number(codLojaRaw);

  return {
    ...item,
    COD_PRODUTO: codProduto,
    QTD_ESTOQUE: qtdEstoque,
    COD_LOJA: Number.isFinite(codLoja as number) ? (codLoja as number) : null,
  };
};

const normalizeStockData = (payload: any): any[] => {
  if (payload == null) return [];

  const stack: any[] = [payload];
  const seen = new Set<any>();
  const stockItems: any[] = [];

  while (stack.length) {
    const current = stack.pop();
    if (current == null || seen.has(current)) continue;
    if (typeof current === 'object') seen.add(current);

    if (Array.isArray(current)) {
      current.forEach((item) => {
        const normalizedItem = normalizeStockItem(item);
        if (normalizedItem) stockItems.push(normalizedItem);
        else if (item && typeof item === 'object') stack.push(item);
      });
      continue;
    }

    if (isPlainObject(current)) {
      Object.values(current).forEach((value) => {
        if (value != null && (Array.isArray(value) || isPlainObject(value))) stack.push(value);
      });
    }
  }

  return stockItems;
};

const normalizeParametroPrecoBaseData = (payload: any): any => {
  if (payload == null) return {};
  if (Array.isArray(payload)) return payload;
  if (!isPlainObject(payload)) return payload;

  const candidates = [payload?.data, payload?.data?.data, payload].filter(isPlainObject) as Record<string, any>[];
  const dataRoot = candidates.find((candidate) =>
    Object.keys(candidate).some((key) => Number.isFinite(Number(key)))
  );

  return dataRoot || payload;
};

const validateVersion = async (resp: any) => {
  if (resp?.data?.versao_app?.length && !window.agp_config?.DISABLE_VALIDATE_VERSION) {
    const currentAmbient = window.agp_config?.AMBIENT;
    const isValidVersion = resp?.data?.versao_app?.some((v: { ambiente: AmbientTypes; versao_app: string }) => {
      const isSameAmbient = v.ambiente === currentAmbient;
      if (!isSameAmbient) return false;

      return pacote.version >= v.versao_app;
    });

    if (!isValidVersion) {
      alert('A versão do app está desatualizada. Vamos atualizar o app para você.');
      await unregisterServiceWorker({ preserveLocalStorage: true, setReloadFlag: false });
      window.location.reload();
      return;
    }
  }
};

export const useDownloadFiles = () => {
  let isLoaded = false;
  let mapedPrices: any = {};
  let products: ProductProps[] = [];
  let eventos: EventsClientProps = {};
  let sortedClients: ClientProps[] = [];
  let allCategories: CategoryProps[] = [];
  let pageBuilder: PageBuilderProps[] = [];
  let promocaoCliente: PrecoPromocaoClienteProps = {};

  const objProduct = {} as CatalogDictProps;
  const objCategory = {} as CategoryDictProps;
  const productUltimaCompra: DicUltimaCompraProps = {};
  const cycleByClient: Record<number, SalesCycleProps[]> = {};
  const clientes: Record<number, (ClientProps & { CYCLE: SalesCycleProps[] })> = {};

  const loading = useRef(false);
  const setDownloadSpeedBpsRef = useRef<(bps: number) => void>(() => {});
  const downloadLabelTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const displayLeaderRef = useRef<string | null>(null);

  const [hasError, setError] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const fileName = '';
  const [isOpenedModal, openModalSync] = useState(false);
  const [noUpdate, setNoUpdate] = useState(false as Boolean);
  const [datasets, setDataSets] = useState([] as DataSetsProps[]);

  const { axiosCtx } = useFetchAxios();
  const db = useDbFunctions();
  const { registerEvent } = useEvents(db);
  const { setCustomer } = useUserContext();
  const setOnDB = db.setOnDB;
  const getOnDB = db.getOnDB;
  const setMultipleOnDBAtomic = db.setMultipleOnDBAtomic;
  const setImagesOnDB = db.setImagesOnDB;
  const { setCampanhaProdutos, setCampanhaClientes, setCampanhaFaixa, setCampanhaTopMix, setIndicadorLoja, setIndicadoresProdutividade } = useClient();
  const { setLastSync, getDate, setDownloadingFileName, setDownloadingFiles, setStartDownloadingFile, downloadSpeedBps, setDownloadSpeedBps, setFileDownload, setFileDownloadProgress, setProgressPercent } = useGlobals();
  progressPercentSetterRef.current = setProgressPercent;
  setDownloadSpeedBpsRef.current = setDownloadSpeedBps;

  const handleDownloadingFileName = useCallback(() => {
    if (isSuccess || !isOpenedModal) {
      if (downloadLabelTimeoutRef.current) clearTimeout(downloadLabelTimeoutRef.current);
      return;
    }

    const listDataBase = getOnStorage('datasets');
    const ind = Math.floor(Math.random() * (listDataBase?.length || 0));
    let label = listDataBase?.[ind]?.DESC_ARQUIVO;
    if (label === fileName) {
      label = listDataBase?.[ind + 1]?.DESC_ARQUIVO || 'Baixando';
    }

    downloadLabelTimeoutRef.current = setTimeout(() => handleDownloadingFileName(), 3000);
  }, [fileName, isOpenedModal, isSuccess]);

  const fetchDatasets = useCallback(
    async (list?: DataSetsProps[]) => {
      var totalSize = 0;
      var downloadedSize = 0;
      var speedLastTime = Date.now();
      var speedLastBytes = 0;
      setDownloadSpeedBpsRef.current(0);
      const downloadEvents = {} as DownloadEventProps;

      const fetchAndSaveJson = async (
        url: string,
        name: string,
        desc: string,
        datasetKey: string,
        type: string,
        skipZipRedirect = false
      ): Promise<DatasetDownloadResult> => {
        if (!displayLeaderRef.current) displayLeaderRef.current = desc;
        if (displayLeaderRef.current === desc) {
          setDownloadingFileName(desc);
          setFileDownloadProgress(formatFileSize(0));
        }

        downloadEvents[name] = { ...(downloadEvents[name] || {}), arquivo: name, dataInicio: getDateTimeMS(), status: 1 };
        setStartDownloadingFile(name);

        try {
          const resp = await fetch(url, { cache: 'no-store' });
          
          if (!skipZipRedirect && isZipContent(resp, url)) {
            console.warn(`[${name}] conteúdo ZIP detectado, redirecionando para fetchAndSaveImages`);
            // Reutiliza a mesma Response — evita segundo fetch do mesmo ZIP (corpo ainda não foi lido).
            return fetchAndSaveImages(resp, name, desc, url, datasetKey, type);
          }
          
          if (resp.status >= 300 || resp.status < 200) {
            void registerEvent({
              stEvento: 0,
              tipoEvento: 'DOWNLOAD',
              obsEvento: `Failed to fetch ${name}: ${resp.statusText}`,
            }).catch(() => {});
            throw new Error(`Failed to fetch ${name}: ${resp.statusText}`);
          }
          
          const contentLength = Number(resp.headers.get('content-length')) || 0;
          totalSize += contentLength;
          let totalArchive = contentLength;
          let downloadedArchive = 0;
          let lastFileDownloadUpdate = 0;

          if (displayLeaderRef.current === desc) {
            setFileDownload(formatFileSize(totalArchive));
            setFileDownloadProgress(formatFileSize(0));
            setProgressPercent(0);
          }

          const reader = resp?.body?.getReader();
          if (!reader) throw new Error('Failed to get response body');
          const decoder = new TextDecoder('utf-8');
          const chunks: string[] = [];

          while (true) {
            const result = await reader.read();
            const { done, value } = result;
            if (done) break;
            const len = value?.byteLength ?? 0;
            downloadedSize += len || 1;
            downloadedArchive += len || 1;
            const now = Date.now();
            if (now - speedLastTime >= SPEED_UPDATE_INTERVAL_MS) {
              const elapsed = (now - speedLastTime) / 1000;
              if (elapsed > 0) {
                const bps = (downloadedSize - speedLastBytes) / elapsed;
                setDownloadSpeedBpsRef.current(bps);
              }
              speedLastTime = now;
              speedLastBytes = downloadedSize;
            }
            if (!displayLeaderRef.current) {
              displayLeaderRef.current = desc;
              setDownloadingFileName(desc);
            }
            if (now - lastFileDownloadUpdate >= PROGRESS_THROTTLE_MS && displayLeaderRef.current === desc) {
              lastFileDownloadUpdate = now;
              setFileDownload(formatFileSize(totalArchive));
              setFileDownloadProgress(formatFileSize(downloadedArchive));
            }
            updateProgress(downloadedArchive, totalArchive, name);
            if (displayLeaderRef.current === desc) updateProgress(downloadedArchive, totalArchive, 'progress');
            if (len) chunks.push(decoder.decode(value, { stream: true }));
          }
          progressLastCall[name] = 0;
          progressLastCall['progress'] = 0;
          if (displayLeaderRef.current === desc) {
            setFileDownload(formatFileSize(totalArchive));
            setFileDownloadProgress(formatFileSize(downloadedArchive));
            updateProgress(downloadedArchive, totalArchive, 'progress');
          }
          updateProgress(downloadedArchive, totalArchive, name);

          if (displayLeaderRef.current === desc) displayLeaderRef.current = null;

          const json = JSON.parse(chunks.join(''));
          chunks.length = 0;
          let data = json?.catalogo ?? json?.preco ?? json;

          if (name === 'imagens') {
            data = typeof data === 'string' ? JSON.parse(data) : data;
            const out: Record<number, string> = {};
            if (Array.isArray(data)) {
              for (let j = 0; j < data.length; j++) {
                const item = data[j];
                out[item.COD_PRODUTO] = item.imagem?.replace(/^data:.*;base64,/i, '');
              }
            }
            data = out;
          }
          if (typeof data === 'string') data = JSON.parse(data);
          if (type === 'estoque') data = normalizeStockData(data);
          if (type === 'parametro_preco_base') data = normalizeParametroPrecoBaseData(data);
          downloadEvents[name] = { ...(downloadEvents[name] || {}), dataFim: getDateTimeMS(), status: 1 };
          return { data, name, datasetKey, type };
        } catch (error) {
          if (displayLeaderRef.current === desc) displayLeaderRef.current = null;
          console.error(`Error fetching ${name}:`, error);
          setError(true);
          downloadEvents[name] = { ...(downloadEvents[name] || {}), dataFim: getDateTimeMS(), status: 0 };
          throw error;
        }
      };

      const fetchAndSaveImages = async (
        resp: Response,
        name: string,
        desc: string,
        url: string,
        datasetKey: string,
        type: string
      ) => {
        if (!displayLeaderRef.current) displayLeaderRef.current = desc;
        if (displayLeaderRef.current === desc) {
          setDownloadingFileName(desc);
          setFileDownloadProgress(formatFileSize(0));
        }
        downloadEvents[name] = { ...(downloadEvents[name] || {}), arquivo: name, dataInicio: getDateTimeMS(), status: 1 };

        setStartDownloadingFile(name);

        console.log('[images] processing zip from first fetch', { url });

        try {
          console.log('[images] response', { status: resp.status, ok: resp.ok, contentType: resp.headers.get('content-type') });

          if (!resp.ok) throw new Error(`Failed to fetch imagens zip: ${resp.statusText}`);
          if (!isZipContent(resp, url)) throw new Error('Conteúdo não é ZIP, fallback para JSON');

          const contentLength = Number(resp.headers.get('content-length')) || 0;
          totalSize += contentLength;
          let totalArchive = contentLength;
          let downloadedArchive = 0;
          let lastFileDownloadUpdate = 0;

          if (displayLeaderRef.current === desc) {
            setFileDownload(formatFileSize(totalArchive));
            setFileDownloadProgress(formatFileSize(0));
            setProgressPercent(0);
          }

          const reader = resp.body?.getReader();
          if (!reader) throw new Error('Failed to get response body');
          const chunks: Uint8Array[] = [];

          while (true) {
            const result = await reader.read();
            const { done, value } = result;
            if (done) break;
            const len = value?.byteLength ?? 0;
            downloadedSize += len || 1;
            downloadedArchive += len || 1;
            const now = Date.now();
            if (now - speedLastTime >= SPEED_UPDATE_INTERVAL_MS) {
              const elapsed = (now - speedLastTime) / 1000;
              if (elapsed > 0) {
                const bps = (downloadedSize - speedLastBytes) / elapsed;
                setDownloadSpeedBpsRef.current(bps);
              }
              speedLastTime = now;
              speedLastBytes = downloadedSize;
            }
            if (!displayLeaderRef.current) {
              displayLeaderRef.current = desc;
              setDownloadingFileName(desc);
            }
            if (now - lastFileDownloadUpdate >= PROGRESS_THROTTLE_MS && displayLeaderRef.current === desc) {
              lastFileDownloadUpdate = now;
              setFileDownload(formatFileSize(totalArchive));
              setFileDownloadProgress(formatFileSize(downloadedArchive));
            }
            updateProgress(downloadedArchive, totalArchive, name);
            if (displayLeaderRef.current === desc) updateProgress(downloadedArchive, totalArchive, 'progress');
            if (value) chunks.push(value);
          }

          progressLastCall[name] = 0;
          progressLastCall['progress'] = 0;
          if (displayLeaderRef.current === desc) {
            setFileDownload(formatFileSize(totalArchive));
            setFileDownloadProgress(formatFileSize(downloadedArchive));
            updateProgress(downloadedArchive, totalArchive, 'progress');
          }
          updateProgress(downloadedArchive, totalArchive, name);
          if (displayLeaderRef.current === desc) displayLeaderRef.current = null;

          const blob = new Blob(chunks as BlobPart[]);
          console.log(`[${name}] zip size (bytes)`, blob.size);

          const zip = await JSZip.loadAsync(blob);
          const fileEntries = Object.entries(zip.files);
          console.log(`[${name}] entries in zip`, fileEntries.length);
          console.log(`[${name}] sample names`, fileEntries.slice(0, 10).map(([n]) => n));

          let manifestMap: Record<number, string> | null = null;
          const manifestEntry = Object.values(zip.files).find((f) => f.name.toLowerCase().endsWith('.json'));
          if (manifestEntry) {
            try {
              const manifestRaw = await manifestEntry.async('string');
              console.log(`[${name}] manifest raw (first 500 chars)`, manifestRaw.slice(0, 500));
              const manifest = JSON.parse(manifestRaw);
              manifestMap = Array.isArray(manifest)
                ? manifest.reduce((acc: Record<number, string>, item: any) => {
                    if (item?.COD_PRODUTO && item?.imagem) acc[item.COD_PRODUTO] = item.imagem;
                    return acc;
                  }, {})
                : Object.entries(manifest).reduce((acc, [key, val]) => {
                    const num = Number(key);
                    if (Number.isFinite(num)) acc[num] = String(val);
                    return acc;
                  }, {} as Record<number, string>);
              console.log(`[${name}] manifest parsed keys`, Object.keys(manifestMap).length);
            } catch (err) {
              console.warn(`[${name}] manifest parse failed`, err);
              manifestMap = null;
            }
          } else {
            console.log(`[${name}] manifest not found`);
          }

          const imagesDict: Record<number, string> = {};
          let logged = 0;

          for (const [filename, file] of fileEntries) {
            if (file.dir || !/\.(webp|png|jpe?g)$/i.test(file.name)) continue;
            const code = getProductCodeFromFilename(
              manifestMap?.[Number(getProductCodeFromFilename(filename))] || filename
            );
            if (!code) continue;
            const imageBlob = await file.async('blob');
            const base64 = await blobToBase64(imageBlob);
            imagesDict[code] = base64.replace(/^data:.*;base64,/i, '');

            if (logged < 5) {
              console.log(`[${name}] saved sample`, {
                filename,
                code,
                blobSize: imageBlob.size,
                mime: imageBlob.type,
              });
              logged++;
            }
          }

          console.log(`[${name}] total images mapped`, Object.keys(imagesDict).length);
          downloadEvents[name] = { ...(downloadEvents[name] || {}), dataFim: getDateTimeMS(), status: 1 };

          return { data: imagesDict, name, datasetKey, type };
        } catch (error) {
          console.warn(`Falha no ZIP de ${name}, tentando JSON. Motivo:`, error);
          downloadEvents[name] = { ...(downloadEvents[name] || {}), dataFim: getDateTimeMS(), status: 0 };
          return fetchAndSaveJson(url, name, desc, datasetKey, type, true);
        }
      };

      const tasks = (list ?? []).map(
        (dataset, index) =>
          () =>
            fetchAndSaveJson(
              dataset.URL_S3,
              `${normalizeDatasetType(dataset.TIPO_ARQUIVO)}-${(dataset as DatasetWithStoreMeta).COD_LOJA ?? 'all'}-${(dataset as DatasetWithStoreMeta).COD_SEGMENTO ?? 'all'}-${index}`,
              getDatasetProgressLabel(dataset as DatasetWithStoreMeta),
              getDatasetKey(dataset, index),
              normalizeDatasetType(dataset.TIPO_ARQUIVO),
            )
      );

      setDownloadingFiles((list ?? []).map((item) => getDatasetProgressLabel(item as DatasetWithStoreMeta)));
      
      const responses = await runWithConcurrency(tasks, MAX_CONCURRENT_DOWNLOADS);
      const concatDownloadEvents = Object.values(downloadEvents).map(evt => `${evt.status} ${evt.arquivo} ${evt.dataInicio?.split('T')[1] || ''}-${evt.dataFim?.split('T')[1] || ''}`).join('|');
      
      void registerEvent({
        tipoEvento: 'DOWNLOAD',
        obsEvento: concatDownloadEvents,
        stEvento: Object.values(downloadEvents).some(evt => evt.status === 0) ? 0 : 1,
        codRca: Number(getOnStorage('cod_pessoa') || 0),
      }).catch(() => {});

      const byDatasetKey: Record<string, DatasetDownloadResult> = {};
      const groupedByType: Record<string, { data: any }> = {};
      const rejected: any[] = [];
      responses.forEach((item) => {
        if (item.status === 'fulfilled') {
          const value = item.value;
          byDatasetKey[value.datasetKey] = value;
          groupedByType[value.type] = {
            data: mergeDataValues(groupedByType[value.type]?.data, value.data),
          };
        }
        if (item.status === 'rejected') rejected.push(item.reason);
      });

      if (rejected.length > 0) {
        console.warn('Promises rejeitadas:', rejected);
      }

      setDownloadSpeedBpsRef.current(0);
      if (!Object.keys(byDatasetKey).length) return undefined;
      return {
        byType: groupedByType,
        byDatasetKey,
      } as FetchDatasetsResponse;
    },
    [getOnDB] 
  );

  const tryCatch = async <T>(fn: () => Promise<T>, msg: string): Promise<T | undefined> => {
    try {
      await fn()
    } catch (error) {
      void registerEvent({
        tipoEvento: 'PROCESSAMENTO',
        obsEvento: `Erro ao carregar [${msg}]: ${error}`,
        stEvento: 0,
      }).catch(() => {});
      return undefined;
    }
    await yieldToMain();
  };

  const loadFiles = useCallback(
    async (newDatasets?: DataSetsProps[], skipGlobalGuard = false) => {
      if (!skipGlobalGuard && globalSyncInProgress) return;
      if (!skipGlobalGuard) globalSyncInProgress = true;
      if (loading.current) return;

      loading.current = true;
      setError(false);
      setIsSuccess(false);
      updateProgress(0, 0, '.');
      try {

        const listToDownload = (newDatasets || []).map(d => ({
          ...d,
          TIPO_ARQUIVO: normalizeDatasetType(d.TIPO_ARQUIVO),
        }));
        if (!listToDownload.length) {
          setNoUpdate(true);
          setIsSuccess(true);
          return;
        }

        console.log('[RCA sync] Iniciando processamento de datasets', {
          total: listToDownload.length,
          arquivos: listToDownload.map((d) => ({
            tipo: d.TIPO_ARQUIVO,
            loja: d.COD_LOJA ?? 'null',
            segmento: d.COD_SEGMENTO ?? 'null',
            desc: d.DESC_ARQUIVO,
          })),
        });

      if (listToDownload.find((i) => i.TIPO_ARQUIVO === 'estoque')) {
        removeFromStorage('stock_left');
      }

      setOnStorage('inconsistencies_data', { active: false, problems: [] });
      const resp = await fetchDatasets(listToDownload);

      void registerEvent({
        tipoEvento: 'PROCESSAMENTO',
        obsEvento: 'Iniciando processamento de dados',
      }).catch(() => {});
      
      // Pega a lista antiga de datasets (imagens ficam em chave separada)
      const oldDatasets = (getOnStorage('datasets') || []).filter((dataset) => !isImageDataset(dataset));
      const oldImageDatasets = migrateImageDatasetsFromMain();
      // Pega os datasets que foram baixados com sucesso
      const successfullyDownloaded = listToDownload.filter((d, index) => resp?.byDatasetKey?.[getDatasetKey(d, index)]);
      const successfullyDownloadedMain = successfullyDownloaded.filter((dataset) => !isImageDataset(dataset));
      const successfullyDownloadedImages = successfullyDownloaded.filter((dataset) => isImageDataset(dataset));

      // Cria um mapa da lista antiga para fácil substituição
      const oldDatasetsMap = oldDatasets.reduce(
        (acc, item) => {
          acc[getDatasetKey(item)] = item;
          return acc;
        },
        {} as Record<string, DataSetsProps>
      );

      // Atualiza o mapa com os arquivos baixados com sucesso
      successfullyDownloadedMain.forEach((item) => {
        oldDatasetsMap[getDatasetKey(item)] = item;
      });

      // Converte o mapa de volta para uma lista e salva
      const finalDatasetList = Object.values(oldDatasetsMap);
      setOnStorage('datasets', finalDatasetList);
      setOnStorage('new_data_sets', finalDatasetList);
      if (successfullyDownloadedImages.length) {
        writeImageDatasets(mergeDatasetsByUrl(oldImageDatasets, successfullyDownloadedImages));
      }
      setDataSets(listToDownload);

      setPreparing(true);

      const datasetsByType = successfullyDownloaded.reduce(
        (acc, dataset) => {
          const type = normalizeDatasetType(dataset.TIPO_ARQUIVO);
          if (!acc[type]) acc[type] = [];
          acc[type].push(dataset as DatasetWithStoreMeta);
          return acc;
        },
        {} as Record<string, DatasetWithStoreMeta[]>
      );

      const finalDatasetsByType = finalDatasetList.reduce(
        (acc, dataset) => {
          const normalized = {
            ...(dataset as DatasetWithStoreMeta),
            TIPO_ARQUIVO: normalizeDatasetType(dataset.TIPO_ARQUIVO),
          } as DatasetWithStoreMeta;
          if (!acc[normalized.TIPO_ARQUIVO]) acc[normalized.TIPO_ARQUIVO] = [];
          acc[normalized.TIPO_ARQUIVO].push(normalized);
          return acc;
        },
        {} as Record<string, DatasetWithStoreMeta[]>
      );

      const hadImagensDownloadInThisRun = successfullyDownloaded.some(
        (d) => normalizeDatasetType(d.TIPO_ARQUIVO) === 'imagens'
      );

      const getData = async <T extends Tables>(dbKey: T, defaultValue: any): Promise<TableTypes[T]> => {
        const key = dbKey === 'dicionario_produtos' ? 'catalogo' : dbKey;
        const incoming = resp?.byType?.[key]?.data;
        if (incoming == null) {
          const fromDb = await getOnDB(dbKey, undefined, undefined, dbKey === 'imagens', false);
          return (fromDb ?? defaultValue) as TableTypes[T];
        }

        const updatedDatasets = datasetsByType[dbKey] || [];
        const expectedDatasets = finalDatasetsByType[dbKey] || [];
        const isPartialSync = expectedDatasets.length > updatedDatasets.length;
        if (!isPartialSync) return incoming as TableTypes[T];

        const fromDb = await getOnDB(dbKey, undefined, undefined, dbKey === 'imagens', false);
        if (dbKey === 'cliente') {
          return mergeClientsByCodePreserveStores(fromDb, incoming) as TableTypes[T];
        }
        if (dbKey === 'dicionario_produtos') {
          const data = fromDb ? Object.values(fromDb) : [];
          const result = mergeCatalogByProductCode(Object.values(data || {}), incoming) as TableTypes[T];
          return result;
        }
        if (dbKey === 'precoPromocaoCliente') {
          return mergePrecoPromocaoClientePartial(fromDb, incoming) as TableTypes[T];
        }
        if (dbKey === 'parametro_preco_base') {
          return mergeParametroPrecoBasePartial(fromDb, incoming) as TableTypes[T];
        }
        return mergeByDatasetScope(fromDb, incoming, updatedDatasets) as TableTypes[T];
      };

      const getEstoqueData = async (): Promise<TableTypes['estoque']> => {
        const fromDb = await getOnDB('estoque');
        const incoming = resp?.byType?.estoque?.data;
        if (incoming == null) {
          return (Array.isArray(fromDb) ? fromDb : []) as TableTypes['estoque'];
        }
        return mergeEstoquePreserveExistingStores(fromDb, incoming) as TableTypes['estoque'];
      };

      const getPrecoData = async () => {
        // Prioriza novo dataset de preço e mantém fallback para o legado.
        if (resp?.byType?.preco_novo?.data != null) {
          const incoming = resp.byType.preco_novo.data;
          const updatedDatasets = datasetsByType.preco_novo || [];
          const expectedDatasets = finalDatasetsByType.preco_novo || [];
          const isPartialSync = expectedDatasets.length > updatedDatasets.length;
          if (!isPartialSync) return incoming;

          const fromDb = await getOnDB('preco');
          return mergePrecoByScope(fromDb, incoming, updatedDatasets);
        }
        return getData('preco', []);
      };

      const parseNumberLoose = (value: any): number | null => {
        if (value === null || value === undefined) return null;
        if (typeof value === 'number') return Number.isFinite(value) ? value : null;
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (!trimmed) return null;
          // Tenta lidar com valores com ',' (pt-BR) e '.' (milhares)
          const normalized = trimmed.includes(',') ? trimmed.replace(/\./g, '').replace(',', '.') : trimmed;
          const num = Number(normalized);
          return Number.isFinite(num) ? num : null;
        }
        return null;
      };

      const parseJSONIfString = <T = any>(value: unknown, fallback: string): T => {
        if (typeof value === 'string') {
          return parseJSON(value || fallback) as T;
        }

        if (value === null || value === undefined) {
          return parseJSON(fallback) as T;
        }

        return value as T;
      };

      // Saldo flex (novo formato):
      // { [COD_PESSOA]: { VLR_ERP } }
      const mapSaldoFlexToNewMap = (src: any): Record<string, { VLR_ERP: number | null }> => {
        if (!src) return {};

        // Alguns backends podem trazer wrapper: { data: {...} } ou { data: { data: {...} } }
        const candidates: any[] = [src, src?.data, src?.data?.data].filter(Boolean);
        const mapRoot = candidates.find((c) => {
          if (!c || typeof c !== 'object' || Array.isArray(c)) return false;
          const keys = Object.keys(c);
          return keys.length > 0 && keys.some((k) => Number.isFinite(Number(k)));
        });

        if (!mapRoot) return {};

        return Object.entries(mapRoot).reduce<Record<string, { VLR_ERP: number | null }>>((acc, [cod, node]) => {
          const codPessoa = Number(cod);
          if (!Number.isFinite(codPessoa)) return acc;

          // novo formato: { [COD_PESSOA]: { VLR_ERP } }
          // tolera também caso venha { [COD_PESSOA]: { VLR_ERP: '...' } } ou valor direto
          const vlrRaw =
            node && typeof node === 'object' ? (node as any).VLR_ERP ?? node : null;

          const parsed = parseNumberLoose(vlrRaw);
          acc[String(codPessoa)] = { VLR_ERP: parsed };
          return acc;
        }, {});
      };

      const getSaldoFlexData = async (): Promise<TableTypes['saldo_flex']> => {
        // Prioriza o novo dataset, e salva diretamente no `saldo_flex` (objeto por COD_PESSOA).
        // Não suportamos mais o formato legado (array).
        const srcNovo = resp?.byType?.saldo_flex_novo?.data;
        if (srcNovo != null) return mapSaldoFlexToNewMap(srcNovo);

        const srcFlex = resp?.byType?.saldo_flex?.data;
        if (srcFlex != null && !Array.isArray(srcFlex)) return mapSaldoFlexToNewMap(srcFlex);

        const fromDb = await getOnDB('saldo_flex');
        if (!fromDb || Array.isArray(fromDb)) return {};
        return mapSaldoFlexToNewMap(fromDb);
      };

      const [
        precos,
        marcas,
        imagens,
        estoque,
        catalogo,
        saldoFlex,
        loadedClients,
        categorias,
        clienteBoletos,
        clienteEventosCortes,
        indicadores,
        campanha,
        listPageBuilder,
        janelaEntrega,
        parametrosAws,
        indicadorLoja,
        campanhaFaixa,
        campanhaProduto,
        campanhaCliente,
        campanhaTopMix,
        parametroPrecoBase,
        recomendacaoBigData,
        precoPromocaoCliente,
        cicloVendaCliente,
        percentualFornecedor,
        substituicaotributaria,
        restricaoProdutoCliente,
        ultimaCompra,
        combo
      ] = await Promise.all([
        getPrecoData(),
        getData('marcas', []),
        hadImagensDownloadInThisRun ? getData('imagens', {}) : Promise.resolve({} as TableTypes['imagens']),
        getEstoqueData(),
        getData('dicionario_produtos', {}).then(data => data as ProductProps[]),
        getSaldoFlexData(),
        getData('cliente', []),
        getData('categoria', []),
        getData('cliente_boletos', {}),
        getData('cliente_cortes', {}),
        getData('indicadores', []),
        getData('campanha_indicador', []),
        getData('pagebuilder', []),
        getData('janela_entrega', []),
        getData('parametros_aws', []),
        getData('indicador_loja', []),
        getData('campanha_faixa', []),
        getData('campanha_produto', {}),
        getData('campanha_cliente', {}),
        getData('campanha_mix_fornecedor', []),
        getData('parametro_preco_base', []),
        getData('recomendacaoBigData', []),
        getData('precoPromocaoCliente', []),
        getData('ciclo_venda_cliente_cliente', []),
        getData('percentual_fornecedor', []),
        getData('cod_regime_tributario', []),
        getData('restricaoProdutoCliente', []),
        getData('ultima_compra', []),
        getData('combo', []),
      ]);

      // Define origem dos dados de preço:
      // - prioriza dataset preco_novo
      // - fallback para preço legado salvo no DB
      const precosSource = precos ?? [];

      const yieldToMain = (): Promise<void> =>
        new Promise((resolve) => {
          if (typeof requestIdleCallback !== 'undefined') {
            requestIdleCallback(() => resolve(), { timeout: 50 });
          } else {
            setTimeout(resolve, 0);
          }
        });

      // Trabalhando com o dataset de eventos do cliente [clienteBoletos]
      await tryCatch(async () => {
        eventos =
          clienteBoletos?.clientes?.reduce((acc, cliente) => {
            return {
              ...acc,
              [cliente.cod_cliente]: {
                boletos: cliente.boletos,
                cod_cliente: cliente.cod_cliente,
              },
            };
          }, {} as EventsClientProps) || {};
      }, 'clienteBoletos');

      // Trabalhando com o dataset de ciclo de venda do cliente [cicloVendaCliente]
      await tryCatch(async () => {
        if (Array.isArray(cicloVendaCliente)) {
          for (let j = 0; j < cicloVendaCliente.length; j++) {
            const cycle = cicloVendaCliente[j];
            const cod = cycle.COD_CLIENTE;
            if (!cycleByClient[cod]) cycleByClient[cod] = [];
            cycleByClient[cod].push(cycle);
          }
          Object.keys(cycleByClient).forEach((cod) => {
            cycleByClient[Number(cod)].sort((a, b) => a.SEMANA_CICLO_ID - b.SEMANA_CICLO_ID);
          });
        }
      }, 'cicloVendaCliente')
  
      // Trabalhando com o dataset de clientes [loadedClients]
      await tryCatch(async () => {
        const grouped = ensureClientsGroupedByLoja(loadedClients || []);
        sortedClients = grouped.sort((a, b) => {
          if (a.NM_FANTASIA > b.NM_FANTASIA) return 1;
          if (a.NM_FANTASIA < b.NM_FANTASIA) return -1;
          return 0;
        });
      }, 'loadedClients');

      // Trabalhando com o dataset de clientes [clientes]
      await tryCatch(async () => {
        for (let i = 0; i < sortedClients.length; i += CLIENTS_CHUNK_SIZE) {
          const chunk = sortedClients.slice(i, i + CLIENTS_CHUNK_SIZE);
          chunk.forEach((c) => {
            c.COD_SEGMENTO = parseJSONIfString(c?.COD_SEGMENTO, '[]');
            c.DT_ULTIMA_COMPRA = parseJSONIfString(c?.DT_ULTIMA_COMPRA, '[]');
            c.UNILEVER = c.FL_UNILEVER === 'True';
            const CYCLE = cycleByClient[c.COD_CLIENTE] || [];
            const currentClient = clientes[c.COD_CLIENTE];
            if (!currentClient) {
              clientes[c.COD_CLIENTE] = { ...c, CYCLE };
              return;
            }

            const mergedLojas = mergeClientLojas(currentClient.lojas, c.lojas);
            clientes[c.COD_CLIENTE] = {
              ...currentClient,
              ...c,
              lojas: mergedLojas,
              CYCLE: CYCLE.length ? CYCLE : currentClient.CYCLE,
            };
          });
          await yieldToMain();
        }
      }, 'clientes');

      // Trabalhando com o dataset de preços [precos]
      await tryCatch(async () => {
        const src: any = precosSource;
        if (!src || typeof src !== 'object') {
          mapedPrices = {};
          return;
        }

        const isLikelyPriceItem = (v: any) => {
          if (!v || typeof v !== 'object') return false;
          // Campos mais comuns no payload de preço
          return (
            v.VLR_PRECO != null ||
            v.VLR_PAUTA != null ||
            v.DESTACA_ST != null ||
            v.COD_TRIBUTACAO_PRODUTO != null ||
            v.COD_PROMOCAO != null
          );
        };

        const out: Record<string, any[]> = {};

        // Formato antigo:
        //   { PRODUTOS: { [COD_PRODUTO]: { [QTD_EMBALAGEM]: PriceItem } }, FILTRO: {...} }
        if (src.PRODUTOS && typeof src.PRODUTOS === 'object') {
          const produtos = src.PRODUTOS || {};
          const filtro = src.FILTRO || {};

          Object.entries(produtos).forEach(([codProd, embalagens]) => {
            out[codProd] = out[codProd] || [];
            Object.entries(embalagens as any).forEach(([qtdEmb, preco]) => {
              const precoItem = preco as any;
              out[codProd].push({
                ...precoItem,
                COD_PRODUTO: Number(codProd),
                QTD_EMBALAGEM: Number(qtdEmb),
                COD_LOJA: filtro.COD_LOJA ?? null,
                NRO_SEGMENTO: filtro.NRO_SEGMENTO ?? null,
                TP_PROMO: filtro.TP_PROMO ?? precoItem.TP_PROMO ?? null,
                FAIXAACRFINANCEIRO: filtro.FAIXAACRFINANCEIRO ?? precoItem.FAIXAACRFINANCEIRO ?? null,
                INDUSAACRESCTABVENDA: filtro.INDUSAACRESCTABVENDA ?? precoItem.INDUSAACRESCTABVENDA ?? null,
              });
            });
          });

          mapedPrices = out;
          return;
        }

        // Formato novo (exemplo do usuário):
        //   { data: { [COD_LOJA]: { [NRO_SEGMENTO]: { [COD_PRODUTO]: { [QTD_EMBALAGEM]: PriceItem } } } } }
        // Além disso, o dataset pode vir com wrapper tipo: { data: {...}, keyPath: 'preco', preco: 1 }
        const candidates: any[] = [src.data, src.data?.data, src];
        const dataRoot = candidates.find((c) => {
          if (!c || typeof c !== 'object') return false;
          const keys = Object.keys(c);
          return keys.some((k) => !Number.isNaN(Number(k)) && k !== 'keyPath' && k !== 'preco');
        });

        if (!dataRoot || typeof dataRoot !== 'object') {
          mapedPrices = {};
          return;
        }

        Object.entries(dataRoot as any).forEach(([codLoja, bySegment]) => {
          if (!bySegment || typeof bySegment !== 'object') return;

          Object.entries(bySegment as any).forEach(([nroSegmento, byProduct]) => {
            if (!byProduct || typeof byProduct !== 'object') return;

            Object.entries(byProduct as any).forEach(([codProd, priceNode]) => {
              if (!priceNode || typeof priceNode !== 'object') return;

              const codLojaNum = Number(codLoja);
              const segNum = Number(nroSegmento);
              if (!Number.isFinite(codLojaNum) || !Number.isFinite(segNum)) return;

              // priceNode costuma ser { [QTD_EMBALAGEM]: PriceItem }
              const embEntries = Object.entries(priceNode as any);
              if (embEntries.length === 0) return;

              const [firstKey] = embEntries[0];
              const firstKeyNum = Number(firstKey);

              // No dataset novo, as chaves de priceNode são quantidades de embalagem.
              if (!Number.isNaN(firstKeyNum)) {
                embEntries.forEach(([qtdEmb, preco]) => {
                  const qtdEmbNum = Number(qtdEmb);
                  if (Number.isNaN(qtdEmbNum) || !preco) return;

                  out[codProd] = out[codProd] || [];
                  out[codProd].push({
                    ...(preco as any),
                    COD_PRODUTO: Number(codProd),
                    QTD_EMBALAGEM: qtdEmbNum,
                    COD_LOJA: codLojaNum,
                    NRO_SEGMENTO: segNum,
                  });
                });
              } else if (isLikelyPriceItem(priceNode)) {
                // Caso raro: priceNode já seja um item único (não mapeado por QTD)
                const qtdEmbNum =
                  (priceNode as any).QTD_EMBALAGEM != null
                    ? Number((priceNode as any).QTD_EMBALAGEM)
                    : (priceNode as any).QTD != null
                      ? Number((priceNode as any).QTD)
                      : null;

                if (qtdEmbNum != null && Number.isFinite(qtdEmbNum)) {
                  out[codProd] = out[codProd] || [];
                  out[codProd].push({
                    ...(priceNode as any),
                    COD_PRODUTO: Number(codProd),
                    QTD_EMBALAGEM: qtdEmbNum,
                    COD_LOJA: codLojaNum,
                    NRO_SEGMENTO: segNum,
                  });
                }
              }
            });
          });
        });

        mapedPrices = out;
      }, 'precos');

      // Trabalhando com o dataset de preços promocionais [precoPromocaoCliente]
      await tryCatch(async () => {
        promocaoCliente = Array.isArray(precoPromocaoCliente)
          ? reducePromocaoClienteArrayToNestedMap(precoPromocaoCliente)
          : precoPromocaoCliente;
      }, 'precoPromocaoCliente');

      // Trabalhando com o dataset de última compra [ultimaCompra]
      await tryCatch(async () => {
        if (Array.isArray(ultimaCompra)) {
          for (let j = 0; j < ultimaCompra.length; j++) {
            const c = ultimaCompra[j];
            productUltimaCompra[c.SEQPRODUTO] = c;
          }
        }
      }, 'ultimaCompra');

      // Trabalhando com o dataset de produtos [catalogo]
      await tryCatch(async () => {
        for (let i = 0; i < catalogo.length; i += CATALOG_CHUNK_SIZE) {
          
          const chunk = catalogo.slice(i, i + CATALOG_CHUNK_SIZE);
          chunk.forEach((item: ProductProps) => {
            // Inclui todo o catálogo no dicionário; preços podem faltar se o dataset de preço
            // vier vazio ou em formato não mapeado — nesse caso PRECO fica [] (evita obj vazio no IDB).
            const PRECO =
              mapedPrices[item.COD_PRODUTO] ??
              mapedPrices[String(item.COD_PRODUTO)] ??
              [];

            const EMBALAGEM = item?.EMBALAGEM?.map((emb) => {
              if (emb?.QTD_MULTIPLO_VENDA > 1) {
                return { ...emb, SG_EMBALAGEM: 'PK' };
              }
              return { ...emb };
            });

            const product = {
              ...item,
              PRECO,
              IMAGEM: '',
              EMBALAGEM,
              UNILEVER: item.FL_UNILEVER === 'True',
              ultimaCompra: productUltimaCompra[item.COD_PRODUTO] || {},
            } as ProductProps;

            const existingProduct = objProduct[item.COD_PRODUTO];
            if (!existingProduct) {
              products.push(product);
              objProduct[item.COD_PRODUTO] = product;
              return;
            }

            const mergedProduct = mergeProductsByCode(existingProduct, product);
            objProduct[item.COD_PRODUTO] = mergedProduct;

            const indexInProducts = products.findIndex((p) => p.COD_PRODUTO === item.COD_PRODUTO);
            if (indexInProducts >= 0) {
              products[indexInProducts] = mergedProduct;
            } else {
              products.push(mergedProduct);
            }
          });
          await yieldToMain();
        }
      }, 'catalogo');

      // Trabalhando com o dataset de categorias [categorias]
      await tryCatch(async () => {
        if (!!categorias?.length) {
          categorias?.forEach((item: CategoryProps) => {
            if (!objCategory[item?.COD_CATEGORIA]) {
              objCategory[item?.COD_CATEGORIA] = {
                ...item,
              };
            }
          });
        }
      }, 'categorias');
    
      // Trabalhando com o dataset de categorias [allCategories]
      await tryCatch(async () => {
        isLoaded = !!categorias.find((c) => {
          return c.PATH_CATEGORIA === campanhaProduto?.url_amigavel;
        });
        allCategories = [...(isLoaded ? [] : mountCategories(campanhaProduto)), ...categorias];
      }, 'allCategories');

      // Trabalhando com o dataset de pagebuilder [listPageBuilder]
      await tryCatch(async () => {
        pageBuilder = listPageBuilder.map((i: any, ind: any) => ({ ...i, ID: toCoded(`${ind + 1}`, true, true) }));
      }, 'listPageBuilder');

      void registerEvent({
        tipoEvento: 'PROCESSAMENTO',
        obsEvento: 'Finalizando processamento de dados',
      }).catch(() => {});

      // dados | chave | deve validar | é obrigatório
      const dbWrites: [data: any, key: Tables, shouldValidate?: boolean, isObrigatory?: boolean][] = [
        [marcas, 'marcas', true, true],
        [precosSource, 'preco', true, true],
        [estoque, 'estoque', true, true],
        [imagens, 'imagens', true],
        [catalogo, 'catalogo', true, true],
        [Object.values(clientes), 'cliente', true, true],
        [products, 'produtos'],
        [saldoFlex, 'saldo_flex', true],
        [pageBuilder, 'pagebuilder', true],
        [indicadores, 'indicadores', true],
        [allCategories, 'categoria', true, true],
        [campanha, 'campanha_indicador', true],
        [parametrosAws, 'parametros_aws', true],
        [janelaEntrega, 'janela_entrega', true],
        [campanhaFaixa, 'campanha_faixa', true],
        [indicadorLoja, 'indicador_loja', true],
        [objProduct, 'dicionario_produtos'],
        [eventos, 'eventos'],
        [campanhaCliente, 'campanha_cliente', true],
        [campanhaProduto, 'campanha_produto', true],
        [objCategory, 'dicionario_categorias'],
        [promocaoCliente, 'precoPromocaoCliente', true],
        [campanhaTopMix, 'campanha_mix_fornecedor', true],
        [parametroPrecoBase, 'parametro_preco_base', true, true],
        [recomendacaoBigData, 'recomendacaoBigData', true],
        [percentualFornecedor, 'percentual_fornecedor', true, true],
        [substituicaotributaria, 'cod_regime_tributario', true, true],
        [cicloVendaCliente, 'ciclo_venda_cliente_cliente', true],
        [restricaoProdutoCliente, 'restricaoProdutoCliente', true],
        [ultimaCompra, 'ultima_compra', true],
        [clienteEventosCortes, 'cliente_cortes', true],
        [clienteBoletos, 'cliente_boletos', true],
        [combo, 'combo', true],
      ];

      const relatorio: { key: Tables; st: number; obs?: string }[] = [];
      const blockingProblems: InconsistencyProblem[] = [];
      let hasError = false;

      const typesAttemptedInRun = new Set(
        listToDownload.map((dataset) => normalizeDatasetType(dataset.TIPO_ARQUIVO))
      );

      const preparedMain: Array<{ name: Tables; data: any }> = [];
      let imagensPayload: any = null;

      for (const entry of dbWrites) {
        const [data, key, shouldValidate, isObrigatory] = entry;
        if (key === 'produtos') continue;

        const total = (Array.isArray(data) ? data.length : Object.keys(data || {}).length) || 0;

        if (shouldValidate && isObrigatory && total === 0) {
          if (wasTableAttemptedInRun(key, typesAttemptedInRun)) {
            const obs = `${OBRIGATORY_EMPTY_BLOCK_REASON} ${key}`;
            relatorio.push({ key, st: 0, obs });
            blockingProblems.push({ key, obs });
            console.log({ key, st: 0, obs: OBRIGATORY_EMPTY_BLOCK_REASON, data });
            hasError = true;
          }
          continue;
        }

        if (shouldValidate && !isObrigatory && total === 0) {
          relatorio.push({ key, st: 0, obs: 'sem dados {N}' });
          continue;
        }

        if (!shouldValidate && total === 0) {
          relatorio.push({ key, st: 0, obs: 'sem dados — escrita ignorada' });
          continue;
        }

        if (key === 'imagens') {
          imagensPayload = data;
          continue;
        }

        preparedMain.push({ name: key, data });
      }

      if (preparedMain.length > 0) {
        try {
          await setMultipleOnDBAtomic(preparedMain);
          preparedMain.forEach(({ name }) => relatorio.push({ key: name, st: 1 }));
        } catch (err: any) {
          hasError = true;

          const msg = err?.message ?? String(err);
          const failedKey = extractAtomicFailureTableKey(msg) ?? preparedMain[0]?.name;
          if (failedKey) {
            blockingProblems.push({ key: failedKey, obs: msg });
          }
          preparedMain.forEach(({ name }) =>
            relatorio.push({ key: name, st: 0, obs: `falha na transação atômica IDB: ${msg}` })
          );
        }
      }

      if (hadImagensDownloadInThisRun && imagensPayload != null && isValidDbPayload(imagensPayload)) {
        try {
          await setImagesOnDB(imagensPayload);
          relatorio.push({ key: 'imagens', st: 1 });
        } catch (err: any) {
          relatorio.push({ key: 'imagens', st: 0, obs: err?.message ?? String(err) });
        }
      }

      if (hasError) {
        setOnStorage('inconsistencies_data', { active: true, problems: blockingProblems });
      }

      void registerEvent({
        tipoEvento: 'DB_FUNCTIONS',
        obsEvento: `Salvando dados: ${relatorio.map((item) => {
          const obs = item.obs ? `[${item.obs}]` : '';
          return `${item.st} ${item.key} ${obs}`
        }).join('|')}`,
        stEvento: hasError ? 0 : 1,
      }).catch(() => {});

      if (downloadLabelTimeoutRef.current) {
        clearTimeout(downloadLabelTimeoutRef.current);
        downloadLabelTimeoutRef.current = null;
      }
      
      const rcaCustomer = getOnStorage('rca_usuario');

      setCustomer(rcaCustomer as CustomerDataProps);
      setCampanhaProdutos(campanhaProduto || {});
      setCampanhaClientes(campanhaCliente || {});
      setCampanhaFaixa(campanhaFaixa || []);
      setCampanhaTopMix(campanhaTopMix || []);
      setIndicadorLoja(indicadorLoja || []);
      setIndicadoresProdutividade(indicadores || []);

      setPreparing(false);
      loading.current = false;

      if (hasError) {
        setError(true);
        setIsSuccess(false);
        console.warn('[RCA sync] Finalizado com erro ao gravar/processar dados');
        return undefined;
      }

        setIsSuccess(true);

        console.log('[RCA sync] Concluído com sucesso', {
          datasetsProcessados: listToDownload.length,
          baixadosComSucesso: successfullyDownloaded.length,
        });

        return resp;
      } finally {
        loading.current = false;
        if (!skipGlobalGuard) {
          globalSyncInProgress = false;
        }
      }
    },
    [
      fetchDatasets,
      getOnDB,
      setDownloadingFileName,
      setOnDB,
      setMultipleOnDBAtomic,
      setImagesOnDB,
      setCustomer,
      setCampanhaProdutos,
      setCampanhaClientes,
      setCampanhaFaixa,
      setCampanhaTopMix,
      setIndicadorLoja,
      setIndicadoresProdutividade,
    ]
  );

  const updateStock = useCallback(async () => {
    if (globalSyncInProgress) return;
    globalSyncInProgress = true;
    try {
      const rcaInfos = getOnStorage('rca_usuario');
      if (!rcaInfos?.codPessoa) return;

      const resp: any = await axiosCtx.get(`/getdataset/${rcaInfos.codPessoa}`, { timeout: GETDATASET_STOCK_TIMEOUT_MS });
      await validateVersion(resp);

      const [representante] = resp?.data?.representante || [];
      const newList: DataSetsProps[] = representante?.link_Dataset || [];

      if (!newList.length) return;

      const oldList: DataSetsProps[] = getOnStorage('datasets') || [];
      const newStockFiles = newList
        .filter((item) => normalizeDatasetType(item.TIPO_ARQUIVO) === 'estoque')
        .map((item) => ({ ...item, TIPO_ARQUIVO: normalizeDatasetType(item.TIPO_ARQUIVO) }));

      if (!newStockFiles.length) return;

      const oldStockKeys = new Set(
        oldList
          .filter((item) => normalizeDatasetType(item.TIPO_ARQUIVO) === 'estoque')
          .map((item) => getDatasetKey({ ...item, TIPO_ARQUIVO: normalizeDatasetType(item.TIPO_ARQUIVO) }))
      );
      const filesToDownload = newStockFiles.filter((item) => {
        const newKey = getDatasetKey(item);
        if (!oldStockKeys.has(newKey)) return true;

        const oldItem = oldList.find((oldDataset) => getDatasetKey(oldDataset as DatasetWithStoreMeta) === newKey);
        return shouldDownloadUpdatedDataset(oldItem, item);
      });

      if (filesToDownload.length) {
        const stockResponse = await fetchDatasets(filesToDownload);
        if (stockResponse?.byType?.estoque?.data != null) {
          const existingStock = await getOnDB('estoque');
          const mergedStock = mergeEstoquePreserveExistingStores(existingStock, stockResponse.byType.estoque.data);
          await setOnDB(mergedStock, 'estoque');
          const updatedOldList = oldList.filter((item) => normalizeDatasetType(item.TIPO_ARQUIVO) !== 'estoque');
          const updatedDataSet = [...updatedOldList, ...newStockFiles];
          setOnStorage('datasets', updatedDataSet);
          setOnStorage('new_data_sets', updatedDataSet);
          setOnStorage('stock_left', {});
        }
      }
    } catch (error) {
      if (isUnauthorizedAxiosError(error)) throw error;
      console.error('Erro ao atualizar o estoque:', error);
      setError(true);
    } finally {
      globalSyncInProgress = false;
    }
  }, [axiosCtx, fetchDatasets, getOnDB, setOnDB]);

  const getLinks = useCallback(
    async ({ isBackground }: { isBackground?: boolean } = { isBackground: false }) => {
      if (globalSyncInProgress) return false;
      globalSyncInProgress = true;
      try {
        setPreparing(true);
        setError(false);
        setIsSuccess(false);
        setNoUpdate(false);

        const rcaInfos = getOnStorage('rca_usuario');
        const codPessoa = rcaInfos?.codPessoa ?? rcaInfos?.cod_pessoa;
        if (!codPessoa) {
          setError(true);
          return;
        }

        if (!isBackground) {
          try {
            localStorage.setItem('login_bootstrap', 'in-progress');
          } catch {}
        }

        let resp: any;
        try {
          resp = await axiosCtx.get(`/getdataset/${codPessoa}`, { timeout: GETDATASET_TIMEOUT_MS });
        } catch (error) {
          if (isUnauthorizedAxiosError(error)) throw error;
          setError(true);
          return;
        }

        await validateVersion(resp);

        const [representante] = resp?.data?.representante || [];
        const list = representante?.link_Dataset || [];

        const oldImageDatasets = migrateImageDatasetsFromMain();
        const oldList: DataSetsProps[] = (getOnStorage('datasets') || []).filter(
          (dataset) => !isImageDataset(dataset)
        );
        const urlSet = getKnownDatasetUrls(oldList);

        const apiImageDatasets = list
          .filter((dataset: DataSetsProps) => isImageDataset(dataset))
          .map((dataset: DataSetsProps) => ({
            ...dataset,
            TIPO_ARQUIVO: normalizeDatasetType(dataset.TIPO_ARQUIVO),
          }));
        const imagesInDb = await getOnDB('imagens', undefined, undefined, true, false);
        const { imageUrlSet } = resolveImageDatasetUrlsForSync({
          storedImageDatasets: oldImageDatasets,
          apiImageDatasets,
          imagesInDb,
        });

        let filteredList = list.filter((dt: DataSetsProps) => {
          if (isImageDataset(dt)) return !imageUrlSet.has(dt.URL_S3);
          return !urlSet.has(dt.URL_S3);
        });

        const listByType = list.reduce((acc: Record<string, DataSetsProps[]>, item: DataSetsProps) => {
          const datasetType = normalizeDatasetType(item.TIPO_ARQUIVO);
          if (!acc[datasetType]) acc[datasetType] = [];
          acc[datasetType].push({ ...item, TIPO_ARQUIVO: datasetType });
          return acc;
        }, {} as Record<string, DataSetsProps[]>);

        const emptyObligatoryDatasets = (
          await Promise.all(
            OBLIGATORY_DB_TABLES.map(async (table) => {
              const item = await getOnDB(table, undefined, undefined, false, false);
              const isFill = Array.isArray(item) ? !!item.length : !!Object.keys(item || {}).length;
              if (isFill) return [];

              return resolveDatasetTypesForTable(table).flatMap((type) => listByType[type] ?? []);
            })
          )
        ).flat();

        appendUniqueDatasets(filteredList, emptyObligatoryDatasets);

        if (filteredList.length === 0) {
          console.log('[RCA sync] getLinks: nenhum arquivo novo para baixar', { isBackground });
        } else {
          console.log('[RCA sync] getLinks: disparando sync', {
            isBackground,
            quantidade: filteredList.length,
            tipos: [...new Set(filteredList.map((d: DataSetsProps) => normalizeDatasetType(d.TIPO_ARQUIVO)))],
          });
        }

        const data = await loadFiles(filteredList, true);

        const formattedDate = getDate();
        setLastSync(formattedDate);
        if (!isBackground) {
          try {
            localStorage.setItem('login_bootstrap', 'done');
          } catch {}
        }

        if (isBackground) return filteredList;

        setTimeout(() => {
          unregisterServiceWorker({ preserveLocalStorage: isBackground });
        }, 100);
        return data;
      } finally {
        setPreparing(false);
        globalSyncInProgress = false;
      }
    },
    [loadFiles, setLastSync, getDate, axiosCtx, getOnDB]
  );

  return {
    loading: loading.current,
    hasError,
    noUpdate,
    fileName,
    datasets,
    preparing,
    isSuccess,
    isOpenedModal,
    downloadSpeedBps,
    isAnySyncInProgress: () => globalSyncInProgress,

    setError,
    loadFiles,
    getLinks,
    updateStock,
    openModalSync,
  };
};
